(() => {
  /* BEZELOR recommendations. <bz-recs data-mode="pdp|cart"> reads a candidate pool printed by the theme
     (snippets/bezelor-reco-pool.liquid) and ranks it for this shopper:
       - same brand, same gender, same movement (automatic / quartz), similar strap and style, similar price
       - what is in the cart (cart mode) or the product being viewed (product mode)
       - recently viewed watches (kept in this browser only)
     A little randomness among equally good matches keeps the picks fresh between visits.
     Product mode also shows a "Recently viewed" row. Cart mode fills the cart's existing card slots. */
  if (customElements.get('bz-recs')) return;

  const KEY = 'bz_rv_v1';
  const readViewed = () => {
    try {
      const list = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  };
  const saveViewed = (item) => {
    try {
      const keep = readViewed().filter((entry) => entry.h !== item.h);
      keep.unshift(item);
      localStorage.setItem(KEY, JSON.stringify(keep.slice(0, 14)));
    } catch (e) { /* storage unavailable: recommendations still work */ }
  };

  const GENDER = ['mens', 'womens', 'couple'];
  const MOVE = ['automatic', 'quartz', 'mechanical'];
  const STRAP = ['leather-strap', 'stainless-steel'];
  const STYLE = ['chronograph', 'diver', 'skeleton', 'tourbillon'];
  const tags = (item) => new Set(String(item.g || '').split(',').filter(Boolean));
  const movement = (item) => {
    const t = tags(item);
    const found = MOVE.find((m) => t.has(m));
    if (found) return found;
    const type = String(item.y || '').toLowerCase();
    return MOVE.find((m) => type.includes(m)) || '';
  };
  const gender = (item) => {
    const t = tags(item);
    return GENDER.find((g) => t.has(g)) || '';
  };

  const profile = (anchors, viewed) => {
    const vendors = new Map();
    const tagCount = new Map();
    const moves = new Map();
    const genders = new Map();
    let logSum = 0;
    let weightSum = 0;
    const add = (item, w) => {
      vendors.set(item.v, (vendors.get(item.v) || 0) + w);
      tags(item).forEach((t) => tagCount.set(t, (tagCount.get(t) || 0) + w));
      const m = movement(item);
      if (m) moves.set(m, (moves.get(m) || 0) + w);
      const g = gender(item);
      if (g) genders.set(g, (genders.get(g) || 0) + w);
      if (item.p > 0) {
        logSum += Math.log(item.p) * w;
        weightSum += w;
      }
    };
    anchors.forEach((item) => add(item, 1));
    viewed.slice(0, 5).forEach((item, index) => add(item, 0.35 - index * 0.04));
    const top = (map) => [...map.entries()].sort((a, b) => b[1] - a[1])[0];
    return {
      vendors,
      tagCount,
      moves,
      gender: top(genders) ? top(genders)[0] : '',
      logPrice: weightSum ? logSum / weightSum : 0
    };
  };

  const score = (item, prof, viewedSet, mode) => {
    let s = 0;
    s += Math.min(3.2, (prof.vendors.get(item.v) || 0) * 3.2);
    const g = gender(item);
    if (prof.gender && g && g !== prof.gender && g !== 'couple') s -= 9;
    if (prof.gender && !g) s -= 0.5;
    const m = movement(item);
    if (m) s += Math.min(2.4, (prof.moves.get(m) || 0) * 2.4);
    const t = tags(item);
    STRAP.forEach((k) => { if (t.has(k)) s += Math.min(1.2, (prof.tagCount.get(k) || 0) * 1.2); });
    STYLE.forEach((k) => { if (t.has(k)) s += Math.min(1.1, (prof.tagCount.get(k) || 0) * 1.1); });
    if (item.p > 0 && prof.logPrice) {
      const d = Math.abs(Math.log(item.p) - prof.logPrice);
      s += 2.6 * Math.max(0, 1 - d / 1.3);
    }
    if (item.c > item.p) s += 0.4;
    if (item.r >= 4.5) s += 0.4;
    if (viewedSet.has(item.h)) s += mode === 'cart' ? 2.2 : -1.2;
    return s + Math.random() * 1.5;
  };

  const rank = (pool, anchors, viewed, mode, count) => {
    const prof = profile(anchors, viewed);
    const viewedSet = new Set(viewed.map((v) => v.h));
    const scored = pool.filter((item) => item.a).map((item) => ({ item, s: score(item, prof, viewedSet, mode) })).sort((a, b) => b.s - a.s);
    const out = [];
    const perVendor = new Map();
    for (const entry of scored) {
      const n = perVendor.get(entry.item.v) || 0;
      if (n >= 3 && scored.length > count * 2) continue;
      out.push(entry.item);
      perVendor.set(entry.item.v, n + 1);
      if (out.length >= count) break;
    }
    return out;
  };

  const esc = (text) => String(text == null ? '' : text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const reEsc = (text) => String(text).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const shortTitle = (item) => String(item.t || '').replace(new RegExp('^' + reEsc(item.v || '') + '\\s*[-–:]?\\s*', 'i'), '') || item.t;
  const meta = (item) => {
    const t = tags(item);
    const bits = [];
    const m = movement(item);
    if (m) bits.push(m.charAt(0).toUpperCase() + m.slice(1));
    if (t.has('leather-strap')) bits.push('Leather strap');
    else if (t.has('stainless-steel')) bits.push('Steel');
    if (bits.length < 2 && t.has('chronograph')) bits.push('Chronograph');
    return bits.slice(0, 2).join(' · ');
  };
  const pct = (item) => (item.c > item.p ? Math.round((1 - item.p / item.c) * 100) : 0);
  const url = (item) => '/products/' + item.h;
  const img = (item, alt) => (item.i ? '<img src="' + esc(item.i) + '" alt="' + esc(alt) + '" width="480" height="480" loading="lazy" decoding="async">' : '');

  const card = (item) => {
    const off = pct(item);
    const bits = meta(item);
    return '<li class="bzr__cell"><a class="bzr-card" href="' + esc(url(item)) + '">' +
      '<span class="bzr-card__media">' + img(item, item.t) + (off >= 5 ? '<span class="bzr-card__badge">-' + off + '%</span>' : '') +
      (item.r > 0 ? '<span class="bzr-card__rate" aria-label="Rated ' + Number(item.r).toFixed(1) + ' out of 5"><i aria-hidden="true">★</i>' + Number(item.r).toFixed(1) + '</span>' : '') + '</span>' +
      '<span class="bzr-card__body">' +
      '<span class="bzr-card__brand">' + esc(item.v) + '</span>' +
      '<span class="bzr-card__title">' + esc(shortTitle(item)) + '</span>' +
      (bits ? '<span class="bzr-card__meta">' + esc(bits) + '</span>' : '') +
      '<span class="bzr-card__foot"><span class="bzr-card__price"><b>' + esc(item.pm) + '</b>' + (off >= 5 ? '<s>' + esc(item.cm) + '</s>' : '') + '</span>' +
      '</span></span></a></li>';
  };
  const mini = (item) =>
    '<li class="bzr__mini"><a class="bzr-mini" href="' + esc(url(item)) + '">' +
    '<span class="bzr-mini__media">' + img(item, item.t) + '</span>' +
    '<span class="bzr-mini__brand">' + esc(item.v) + '</span>' +
    '<span class="bzr-mini__title">' + esc(shortTitle(item)) + '</span>' +
    '<b class="bzr-mini__price">' + esc(item.pm) + '</b></a></li>';

  /* ---------- cart picks: one product per brand: what you viewed, then what sells best, then the closest match to your cart ---------- */
  const POOL_KEY = 'bz_cpool_v2';
  const rootUrl = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cachedPool = () => {
    try {
      const c = JSON.parse(sessionStorage.getItem(POOL_KEY) || 'null');
      if (c && c.t && Date.now() - c.t < 30 * 60 * 1000 && Array.isArray(c.pool) && c.pool.length) return c.pool;
    } catch (e) { /* ignore */ }
    return null;
  };
  let poolPromise = null;
  const loadPool = () => {
    const hit = cachedPool();
    if (hit) return Promise.resolve(hit);
    if (!poolPromise) {
      poolPromise = fetch(rootUrl() + '?sections=bezelor-cart-pool', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((j) => {
          const m = /<script[^>]*data-bz-cart-pool[^>]*>([\s\S]*?)<\/script>/.exec(j['bezelor-cart-pool'] || '');
          const pool = m ? JSON.parse(m[1]).pool : null;
          if (!pool || !pool.length) throw new Error('pool');
          try { sessionStorage.setItem(POOL_KEY, JSON.stringify({ t: Date.now(), pool })); } catch (e) { /* ignore */ }
          return pool;
        })
        .catch(() => { poolPromise = null; return []; });
    }
    return poolPromise;
  };
  const expand = (pool) => {
    const map = new Map();
    pool.forEach((e) => {
      let x = map.get(e.h);
      if (!x) {
        if (!e.it) return;
        x = Object.assign({}, e.it);
        map.set(e.h, x);
      }
      if (e.s === 'sd') x.sd = true;
      if (e.s === 'bs') x.bs = e.n;
    });
    return [...map.values()];
  };

  const MAX_PICKS = 6;
  const pickCart = (pool, anchors, cartSet, viewed) => {
    const prof = profile(anchors, viewed);
    const viewedSet = new Set(viewed.map((v) => v.h));
    const g = prof.gender;
    const items = expand(pool).filter((i) => i.a && i.v && !cartSet.has(i.h) && !(g && gender(i) && gender(i) !== g && gender(i) !== 'couple'));
    const byScore = (list) => list.map((i) => ({ i, s: score(i, prof, viewedSet, 'cart') })).sort((a, b) => b.s - a.s).map((x) => x.i);
    const used = new Set();
    const out = [];
    const take = (i, tag) => {
      if (!i || used.has(i.v)) return false;
      used.add(i.v);
      out.push(Object.assign({}, i, { tag }));
      return true;
    };
    const A = viewed.map((v) => items.find((i) => i.h === v.h)).filter(Boolean);
    const B = items.filter((i) => i.bs != null).sort((x, y) => x.bs - y.bs);
    const C = byScore(items);
    const next = (list, tag) => { while (list.length) { if (take(list.shift(), tag)) return true; } return false; };
    let turn = 0;
    while (out.length < MAX_PICKS && (A.length || B.length)) {
      const ok = turn % 2 === 0 ? (next(A, 'viewed') || next(B, 'bs')) : (next(B, 'bs') || next(A, 'viewed'));
      turn += 1;
      if (!ok && !A.length && !B.length) break;
    }
    while (out.length < MAX_PICKS && C.length) take(C.shift(), 'pick');
    return out;
  };

  const rowTag = (item) => {
    if (item.tag === 'viewed') return 'You viewed this';
    if (item.tag === 'bs') return item.bs < 10 ? 'Top seller' : 'Best seller';
    return 'Matches your cart';
  };
  const cartRow = (item) => {
    const off = pct(item);
    return '<article class="bzs-row">' +
      '<a class="bzs-row__media" href="' + esc(url(item)) + '" tabindex="-1" aria-hidden="true">' + img(item, '') + (off >= 5 ? '<span class="bzs-row__off">-' + off + '%</span>' : '') + '</a>' +
      '<div class="bzs-row__body"><span class="bzs-row__tag">' + rowTag(item) + '</span>' +
      '<a href="' + esc(url(item)) + '" class="bzs-row__title">' + esc(item.t) + '</a>' +
      '<span class="bzs-row__price"><b>' + esc(item.pm) + '</b>' + (off >= 5 ? '<s>' + esc(item.cm) + '</s>' : '') + '</span></div>' +
      '<form class="bzs-form" data-bzr-add="' + item.vi + '"><button type="submit" class="bzs-add" aria-label="Choose options for ' + esc(item.t) + '"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>Add</button></form></article>';
  };

  /* fallback when the variant picker (bezelor-quick-add.js) is not loaded */
  const addToCart = async (form) => {
    const button = form.querySelector('button');
    const id = Number(form.dataset.bzrAdd);
    if (!id || !button) return;
    button.disabled = true;
    try {
      const response = await fetch(rootUrl() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id, quantity: 1, sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname })
      });
      const data = await response.json();
      const drawer = document.querySelector('cart-drawer');
      if (!response.ok || !data.sections || !drawer || typeof drawer.renderContents !== 'function') throw new Error('add');
      drawer.renderContents(data);
    } catch (e) {
      window.location.href = '/cart';
    }
  };

  /* warm the pool in the background so the cart opens with picks already in place */
  const warm = () => { if (!cachedPool()) loadPool(); };
  if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 4000 });
  else setTimeout(warm, 2500);

  class BzRecs extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bzrInit) return;
      if (this.dataset.mode === 'cart') {
        this.dataset.bzrInit = '1';
        this.renderCart();
        return;
      }
      const run = () => {
        const script = this.querySelector('script[data-bz-pool]') || this.parentElement.querySelector('script[data-bz-pool]');
        if (!script) return false;
        let data;
        try {
          data = JSON.parse(script.textContent);
        } catch (e) {
          return false;
        }
        this.dataset.bzrInit = '1';
        this.render(data);
        return true;
      };
      if (!run()) requestAnimationFrame(run);
    }

    renderCart() {
      const rail = this.querySelector('[data-bzr-rail]');
      const wrap = this.closest('.bzs');
      let info = { cart: [], anchors: [] };
      try { info = JSON.parse(this.querySelector('script[data-bz-anchors]').textContent); } catch (e) { /* empty cart */ }
      const cartSet = new Set(info.cart || []);
      const viewed = readViewed().filter((v) => !cartSet.has(v.h));
      const show = (pool) => {
        const picks = pool && pool.length ? pickCart(pool, info.anchors || [], cartSet, viewed) : [];
        if (!picks.length) { if (wrap) wrap.hidden = true; return; }
        rail.innerHTML = picks.map(cartRow).join('');
      };
      rail.addEventListener('submit', (event) => {
        const form = event.target.closest('[data-bzr-add]');
        if (!form) return;
        event.preventDefault();
        addToCart(form);
      });
      const hit = cachedPool();
      if (hit) show(hit);
      else loadPool().then(show);
    }

    render(data) {
      const mode = this.dataset.mode || data.mode || 'pdp';
      const anchors = data.anchors || [];
      const pool = data.pool || [];
      const current = mode === 'pdp' ? anchors[0] : null;
      if (current) saveViewed(current);
      const viewed = readViewed().filter((entry) => !current || entry.h !== current.h);

      const count = Number(this.dataset.count) || 4;
      const picks = rank(pool, anchors, viewed, 'pdp', count);
      if (!picks.length) {
        this.hidden = true;
        return;
      }
      const pickedSet = new Set(picks.map((p) => p.h));
      const recent = viewed.filter((v) => !pickedSet.has(v.h)).slice(0, 8);
      this.querySelector('[data-bzr-grid]').innerHTML = picks.map(card).join('');
      const recentBox = this.querySelector('[data-bzr-recent]');
      if (recentBox) {
        if (recent.length >= 2) {
          recentBox.querySelector('[data-bzr-rail]').innerHTML = recent.map(mini).join('');
          recentBox.hidden = false;
        } else recentBox.hidden = true;
      }
      this.dataset.ready = 'true';
    }
  }
  customElements.define('bz-recs', BzRecs);
})();
